"use client";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { createResumeRevamp } from "api/resume-writing/createResumeRevamp";
import Image from "next/image";
import { useState, ChangeEvent, useEffect } from "react";
import { Status } from "../_components/constants";
import { getResumeRevamp } from "api/resume-writing/getResumeRevamp";
import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import { selectAccessToken, setIsAuthenticationOpen } from "lib/redux/uiSlice";

const SubmitResumePage = () => {
  const [revampResponse, setRevampResponse] = useState<any>(null);
  const [file, setFile] = useState<File | null>(null);
  const [phoneNumber, setphoneNumber] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [hasNonPdfFile, setHasNonPdfFile] = useState(false);
  const hasFile = Boolean(file);
  const accessToken = useAppSelector(selectAccessToken);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!accessToken) {
      dispatch(setIsAuthenticationOpen(true));
    }
    const fetchStatus = async () => {
      try {
        const response = await getResumeRevamp();
        setRevampResponse(response);
        return response.status;
      } catch (error) {
        console.error(error);
      }
    };
    fetchStatus().then((resumeStatus) => {
      if (resumeStatus === Status.notSubscribed) {
        window.location.href = "/resume-writing";
      } else if (
        resumeStatus === Status.progress ||
        resumeStatus === Status.review ||
        resumeStatus === Status.completed
      ) {
        window.location.href = "/resume-writing/resume-status";
      }
    });
  }, [dispatch, accessToken]);

  const handleDragLeave = (
    e: React.DragEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>
  ) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);

    const newFile = e.dataTransfer.files[0];

    if (newFile.name.endsWith(".pdf")) {
      setHasNonPdfFile(false);
      setFile(newFile);
    } else {
      setHasNonPdfFile(true);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFile = files[0];
    console.debug("Uploaded Resume...", newFile);
    setFile(newFile);
  };

  const handleRemoveFile = () => {
    setFile(null);
  };

  const handleSubmit = async () => {
    if (!accessToken) {
      dispatch(setIsAuthenticationOpen(true));
      return;
    }

    if (!file || !phoneNumber) {
      alert("Please provide both a resume and a mobile number.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append(
        "revamp",
        new Blob([JSON.stringify({ phoneNumber, remarks: "Test Remarks" })], {
          type: "application/json",
        })
      );
      console.debug("Sending Resume...", file);
      formData.append("file", file);
      const response = await createResumeRevamp(formData);
      console.debug("response", response);
      window.location.href = "/resume-writing/resume-status";
    } catch (error) {
      console.error("Error submitting resume:", error);
    }
  };

  return (
    <main className="flex flex-col items-center justify-center bg-gray-100 max-lg:px-10 max-md:px-5 lg:min-h-[calc(100vh-80px)]">
      <div className="my-10 flex w-full flex-col items-center gap-6 rounded-lg bg-white p-8 shadow-md lg:w-2/3">
        <h1 className="text-xl font-bold max-md:text-center">
          Please provide your current resume
        </h1>
        <p className="-mt-4 text-xs text-gray-500 max-md:text-center">
          Select relevant document to complete the first step
        </p>
        <div
          className={`h-[288px] w-full border-2 md:w-[531px] ${
            dragActive ? "border-blue-500" : "border-gray-300"
          } flex items-center justify-center rounded-lg border-dashed bg-white`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {hasFile ? (
            <div className="flex items-center gap-4">
              <p className="text-center text-gray-700">
                {/* {(resumeFile as any).name} */}
                {file?.name}
              </p>
              <button
                onClick={handleRemoveFile}
                className="rounded-full p-1 hover:bg-gray-100"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          ) : (
            <label
              htmlFor="fileUpload"
              className="flex cursor-pointer flex-col items-center"
            >
              <Image
                src="/assets/resume-writing/drag-and-drop.png"
                width={48}
                height={48}
                alt="Drag-and-drop-icon"
              />
              <p className="mt-5 text-center">
                <span className="hidden lg:inline-block">Drag & Drop or </span>{" "}
                Browse PDF File
              </p>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("fileUpload")?.click();
                }}
                className="mt-3 rounded-3xl border border-[#0F91D2] px-5 py-2 text-xs text-[#0F91D2] md:text-sm"
              >
                Browser File
              </button>
              {hasNonPdfFile && (
                <p className="mt-3 font-medium text-red-400">
                  Only pdf file is supported
                </p>
              )}
              <input
                id="fileUpload"
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={handleFileChange}
              />
            </label>
          )}
        </div>
        <div className="mt-3 flex flex-col items-center gap-2 text-[#666666]">
          <label htmlFor="phoneNumber" className="text-sm">
            Contact Number
          </label>
          <input
            type="text"
            placeholder="Enter Mobile Number"
            value={phoneNumber}
            onChange={(e) => setphoneNumber(e.target.value)}
            className="w-[270px] rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 md:w-96"
          />
        </div>
        <div className="mt-4 flex gap-2 text-[11px] md:ml-auto md:text-base">
          {revampResponse?.resumeUrl && (
            <a
              href={revampResponse?.resumeUrl}
              download="Resume.pdf"
              className="w-fit rounded-3xl border border-blue-500 px-5 py-2 text-blue-500 hover:bg-blue-600 hover:text-white"
            >
              Previous Resume
            </a>
          )}
          <button
            onClick={handleSubmit}
            className="w-fit rounded-3xl bg-blue-500 px-5 py-2 text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Upload
          </button>
        </div>
      </div>
    </main>
  );
};

export default SubmitResumePage;
